
import { defineComponent, watch, reactive, computed, onMounted, ref } from 'vue';
import { userStore } from '@/store/user-store';
import { calendarStore } from '@/store/calendar-store';
import { Form as ValidationForm } from 'vee-validate';
import Message from 'primevue/message';
import Login from '@/components/user/Login.vue';
import OverlayPanel from 'primevue/overlaypanel';

// eslint-disable-next-line
//@ts-ignore
import FormControlInput from '@/components/general/FormControl/Input';
// eslint-disable-next-line
//@ts-ignore
import FormControlTextarea from '@/components/general/FormControl/TextArea';
// eslint-disable-next-line
//@ts-ignore
import FormControlAttributes from '@/components/general/FormControl/Attributes';
// eslint-disable-next-line
//@ts-ignore
import FormControlSlider from '@/components/general/FormControl/Slider';
// eslint-disable-next-line
//@ts-ignore
import FormControlCheckbox from '@/components/general/FormControl/Checkbox.vue';
import { CalendarConfigInterface, MessageInterface } from '@/models/calendar.model';
import { domainStore } from '@/store/domain-store';
import InfoRequiredFields from '../general/InfoRequiredFields.vue';

export default defineComponent({
  name: 'Attendance',
  components: {
    ValidationForm,
    FormControlInput,
    FormControlTextarea,
    FormControlSlider,
    FormControlAttributes,
    FormControlCheckbox,
    InfoRequiredFields,
    Message,
    OverlayPanel,
    Login,
  },
  props: {
    assignedAttributes: {
      required: false,
      type: Array,
      default: () => [],
    },
    privacyPageUrl: {
      // If not given, use url from endpoint as fallback
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props) {
    const form = reactive({
      name: '',
      email: '',
      comment: '',
      countAttendees: null,
    });
    const loginOP = ref<any>(null);
    const attributeValuesByName = reactive<any>({});

    const showAttendance = ref<boolean>(false);
    const showForm = ref<boolean>(true);
    const errMessageAttendees = ref<string>('');
    //computed
    const domainid = computed(() => {
      return domainStore.getState().domainid;
    });
    const isLoggedIn = computed((): boolean => {
      return userStore.isLoggedIn() ? true : false;
    });
    const isLoading = computed((): boolean => {
      return calendarStore.getState().isLoadingContent;
    });
    const attendanceMessage = computed((): MessageInterface | null | undefined => {
      return calendarStore.getState().attendanceMessage;
    });
    const messages = computed((): MessageInterface[] => {
      if (attendanceMessage.value) {
        return [
          {
            severity: attendanceMessage.value.severity,
            content: attendanceMessage.value.content,
          },
        ];
      }
      return [];
    });
    const calendarConfig = computed((): CalendarConfigInterface => {
      return calendarStore.getState().calendarConfig;
    });
    const maxAttendeesAllowed = computed((): number => {
      const maxAttendees = calendarStore.getState().calendarConfig.max_attendees;
      return maxAttendees && maxAttendees > 0 ? maxAttendees : 99;
    });
    const attendeesFieldLabel = computed(() => {
      const maxAttendees = calendarStore.getState().calendarConfig.max_attendees;
      let text = domainStore.translate('calendar.attendance.form.attendees-label');
      if (maxAttendees && maxAttendees > 0) {
        text += ' ' + domainStore.translate('calendar.attendance.form.attendees-max-info', { count: maxAttendees });
      }
      return text;
    });
    const isAttendand = computed((): number | undefined => {
      return calendarStore.getState().singleEvent.attendanceid;
    });
    const maxAttendants = computed(() => {
      return calendarStore.getState().singleEvent.max_attendants;
    });
    const totalAttendants = computed(() => {
      return calendarStore.getState().singleEvent.attendance_count_total || 0;
    });
    const showRequiredFieldsInfo = computed(() => {
      if (!isLoggedIn.value) {
        // Email is mandatory
        return true;
      }
      if (
        calendarConfig.value.name_required ||
        calendarConfig.value.comment_required ||
        calendarConfig.value.count_attendees_required
      ) {
        // registration fields are mandatory if set
        return true;
      }
      if (props.assignedAttributes.find((x: any) => x.required) != undefined) {
        // at least 1 attribute field is mandatory
        return true;
      }
      return false;
    });
    const dsgvoText = computed(() => {
      let text = domainStore.translate('calendar.attendance.form.privacy-checkbox-label');
      let dsgvoUrl = props.privacyPageUrl;
      if (!dsgvoUrl || dsgvoUrl == '') {
        dsgvoUrl = calendarStore.getState().singleEvent.privacy_url || '';
      }
      if (dsgvoUrl && dsgvoUrl !== '') {
        text +=
          ' <a target="_blank" href="' +
          dsgvoUrl +
          '">' +
          domainStore.translate('calendar.attendance.form.privacy-checkbox-url-title') +
          '</a>';
      }
      return text;
    });

    //methods:
    const onValidatedFormField = (fieldValue: any) => {
      // console.log("onValidatedFormField", fieldValue);
      attributeValuesByName[fieldValue.name] = fieldValue.value;
    };

    const onAttendeesFilled = (fieldValue: any) => {
      if (fieldValue.validated) {
        errMessageAttendees.value = '';
      } else {
        errMessageAttendees.value = domainStore.translate('calendar.attendance.form.attendees-max-error', {
          count: maxAttendeesAllowed.value + '',
        });
      }
    };

    const onSubmit = (values: any) => {
      if (values.attendComment) {
        values.comment = values.attendComment;
      }
      calendarStore
        .addAttendance(values, isLoggedIn.value)
        .then(() => {
          showForm.value = false;
        })
        .catch((error: any) => {
          console.log(error.response);
        });
    };
    const resetMessage = () => {
      // wtf why is this decided by the error message....
      // change to key? and then use it in translate?
      if (attendanceMessage.value?.content === 'calendar.message.already-attending-event') {
        showAttendance.value = !showAttendance.value;
      }
      calendarStore.setAttendanceMessage(null);
    };
    const onAttendanceBtnClicked = (ev: any) => {
      if (calendarConfig.value.user_required && !isLoggedIn.value) {
        loginOP.value.show(ev);
      } else {
        showAttendance.value = !showAttendance.value;
      }
    };
    //lifehooks
    onMounted(() => {
      if (userStore.getState().user) {
        form.name = userStore.getState().user!.internal_name;
        form.email = userStore.getState().user!.email;
        calendarStore.getSelfAttendance();
      }
    });
    watch(userStore.getState(), () => {
      // if user is logged in, attendance fields get filled, if he already submitted attendance, he gets response
      if (userStore.getState().user) {
        form.name = userStore.getState().user!.internal_name;
        form.email = userStore.getState().user!.email;
        calendarStore.getSelfAttendance();
      } else {
        form.name = '';
        (form.email = ''), calendarStore.setAttendanceMessage(null);
      }
    });

    // T22959 auto scroll to the alert message element when attendanceMessage changes
    watch(
      () => calendarStore.getState().attendanceMessage,
      (nm, om) => {
        if (nm != null && nm.content != '' && (om == null || om.content != nm.content)) {
          const alertMessageElem = document.getElementById('attendance-alert-messages');
          alertMessageElem?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    );

    // //close attendanceForm on event-change
    watch(
      () => calendarStore.getState().singleEvent,
      (oV, nV) => {
        calendarStore.getSelfAttendance();
        if (oV.id !== nV.id) {
          showAttendance.value = false;
        }
      },
      { deep: true }
    );

    return {
      domainStore,
      form,
      loginOP,
      domainid,
      attendanceMessage,
      errMessageAttendees,
      calendarConfig,
      messages,
      showAttendance,
      maxAttendants,
      maxAttendeesAllowed,
      attendeesFieldLabel,
      totalAttendants,
      isLoading,
      isAttendand,
      showForm,
      isLoggedIn,
      dsgvoText,
      showRequiredFieldsInfo,
      onValidatedFormField,
      onAttendanceBtnClicked,
      onAttendeesFilled,
      onSubmit,
      resetMessage,
    };
  },
});
